import {
  Feather,
  Ionicons
} from '@expo/vector-icons';
import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Meta from '../components/Meta';
import PaperCard from '../components/PaperCard';
import { PaperResult, searchPapers, HistoryItem, fetchHistory } from '../services/paperApi';
import {
  ActivityIndicator,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View
} from 'react-native';


export default function Index() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PaperResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<Set<number>>(new Set());
  const [expandedQueryTexts, setExpandedQueryTexts] = useState<Set<number>>(new Set());

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const items = await fetchHistory(20, true);
        setHistoryItems(items);
      } catch (err) {
        console.error('Failed to load history:', err);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    loadHistory();
  }, []);

  const toggleHistoryItem = (requestId: number) => {
    setExpandedHistoryItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const toggleQueryText = (requestId: number) => {
    setExpandedQueryTexts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };



  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const searchResults = await searchPapers(query);
      setResults(searchResults);
      setQuery('');
    } catch (err) {
      console.error(err);
      setError('Failed to search papers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (query.trim()) {
      handleSearch();
    }
  };


  return (
    <View style={styles.container}>
      <Meta 
        title="Citation Needed - Find the Paper. Back the Claim."
        description="Find scientific papers referenced in any text or article. Free academic paper discovery tool for researchers and students."
      />
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.contentWrapper}>
          <Header />

          <View style={styles.searchSection}>
            <TextInput
              value={query}
              onChangeText={setQuery}
              placeholder="Paste article text, claims, or URL here..."
              style={styles.textInput}
              multiline={true}
              numberOfLines={4}
              placeholderTextColor="#94a3b8"
              editable={!loading}
            />

            <TouchableOpacity
              style={[
                styles.searchButton,
                (!query.trim() || loading) && styles.searchButtonDisabled
              ]}
              onPress={handleSubmit}
              disabled={!query.trim() || loading}
            >
              {loading ? (
                <View style={styles.searchButtonContent}>
                  <ActivityIndicator size="small" color="white" />
                  <Text style={styles.searchButtonText}>Searching...</Text>
                </View>
              ) : (
                <View style={styles.searchButtonContent}>
                  <Ionicons name="search" size={20} color="white" />
                  <Text style={styles.searchButtonText}>Find Academic Paper</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>

          {error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          ) : null}

          {hasSearched && !loading && results.length === 0 && !error && (
            <View style={styles.noResults}>
              <View style={styles.noResultsIcon}>
                <Ionicons name="search" size={32} color="#94a3b8" />
              </View>
              <Text style={styles.noResultsTitle}>No papers found</Text>
              <Text style={styles.noResultsText}>
                Try a different search query or check your text for scientific claims.
              </Text>
            </View>
          )}

          {results.length > 0 && (
            <View style={styles.resultsSection}>
              {results.map((paper, index) => (
                <PaperCard key={paper.work_id || `paper-${index}`} paper={paper} />
              ))}
            </View>
          )}

          {historyLoading ? (
            <View style={styles.historySection}>
              <ActivityIndicator size="small" color="#64748b" />
            </View>
          ) : historyItems.length > 0 ? (
            <View style={styles.historySection}>
              <Text style={styles.historyTitle}>Recent Searches</Text>
              {historyItems.map((item) => {
                const isExpanded = expandedHistoryItems.has(item.request_id);
                const isQueryExpanded = expandedQueryTexts.has(item.request_id);
                const lineCount = item.input_text.split('\n').length;
                const shouldShowMore = item.input_text.length > 100 || lineCount > 2;
                
                
                return (
                  <View key={item.request_id} style={styles.historyItem}>
                    <TouchableOpacity 
                      onPress={() => toggleHistoryItem(item.request_id)}
                      style={styles.historyItemHeader}
                    >
                      <View style={styles.historyItemClickable}>
                        <Ionicons 
                          name={isExpanded ? "chevron-down" : "chevron-forward"} 
                          size={16} 
                          color="#64748b" 
                        />
                        <View style={styles.queryTextContainer}>
                          <View style={styles.queryQuoteBar} />
                          <View style={styles.queryTextContent}>
                            <Text 
                              style={styles.queryText} 
                              numberOfLines={isExpanded ? (isQueryExpanded ? undefined : 2) : 1}
                            >
                              {item.input_text}
                            </Text>
                            {isExpanded && shouldShowMore && (
                              <TouchableOpacity 
                                onPress={(e) => {
                                  e.stopPropagation();
                                  toggleQueryText(item.request_id);
                                }}
                                style={styles.showMoreButton}
                              >
                                <Text style={styles.showMoreText}>
                                  {isQueryExpanded ? 'Show less' : 'Show more'}
                                </Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        </View>
                      </View>
                    </TouchableOpacity>
                    
                    {isExpanded && item.success && (
                      <View style={styles.historyItemExpanded}>
                        <PaperCard 
                          style={{ margin: 4, marginBottom: 4 }}
                          paper={{
                            title: item.title,
                            authors: item.authors,
                            venue: item.venue,
                            year: item.year,
                            is_open_access: item.is_open_access === null ? null : item.is_open_access === 1,
                            paper_url: item.paper_url,
                            pdf_url: item.pdf_url,
                            work_id: item.work_id,
                            work_id_type: item.work_id_type,
                            confidence: item.confidence,
                            source: item.source,
                            identifiers: item.identifiers,
                            key_terms: item.key_terms
                          }} />
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ) : null}

          <Footer />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
  },
  contentWrapper: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 896 : '100%',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'ios' ? 60 : 40,
    paddingBottom: 32,
  },
  searchSection: {
    marginBottom: 48,
  },
  textInput: {
    backgroundColor: 'white',
    borderWidth: 2,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    minHeight: 120,
    textAlignVertical: 'top',
    marginBottom: 16,
  },
  searchButton: {
    backgroundColor: '#2563eb',
    paddingVertical: 14,
    paddingHorizontal: 32,
    borderRadius: 12,
    alignSelf: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  searchButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  searchButtonDisabled: {
    backgroundColor: '#cbd5e1',
    shadowOpacity: 0,
    elevation: 0,
  },
  searchButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
  },
  errorContainer: {
    backgroundColor: '#fef2f2',
    borderLeftWidth: 4,
    borderLeftColor: '#ef4444',
    padding: 16,
    borderRadius: 8,
    marginBottom: 32,
  },
  errorText: {
    color: '#b91c1c',
    fontSize: 16,
  },
  noResults: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  noResultsIcon: {
    width: 64,
    height: 64,
    backgroundColor: '#f1f5f9',
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '500',
    color: '#0f172a',
    marginBottom: 8,
  },
  noResultsText: {
    color: '#64748b',
    fontSize: 16,
    textAlign: 'center',
  },
  resultsSection: {
    marginBottom: 32,
  },
  resultsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  resultsTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  resultsCount: {
    fontSize: 14,
    color: '#94a3b8',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  historySection: {
    marginTop: 32,
    marginBottom: 32,
  },
  historyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 16,
  },
  historyItem: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
  },
  historyItemHeader: {
    padding: 12,
  },
  historyItemClickable: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  queryTextContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  queryQuoteBar: {
    width: 3,
    backgroundColor: '#cbd5e1',
    borderRadius: 1.5,
    marginRight: 8,
    alignSelf: 'stretch',
    minHeight: 20,
  },
  queryTextContent: {
    flex: 1,
  },
  queryText: {
    fontSize: 14,
    color: '#475569',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    lineHeight: 20,
  },
  showMoreButton: {
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  showMoreText: {
    fontSize: 12,
    color: '#2563eb',
    fontWeight: '500',
  },
  historyItemExpanded: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    padding: 16,
    backgroundColor: '#f1f5f9',
  },
});